import { ssoInstance } from './sso';
import { CurrentUser } from 'src/app/models/portal.models';

/**
 * Mirrors the SSO session and the portal identity into localStorage under
 * provider-agnostic keys, so guards/components read plain values.
 *
 * Two sources, applied in order at boot (see app.module.ts):
 *  1. `syncFromSso()` — token + email/name from the identity provider. The client
 *     role in the token (`resource_access.{client}.roles`) is only a hint used to
 *     bootstrap; the portal API is the source of truth for role and tenant.
 *  2. `applyPortalIdentity()` — role + tenant from `GET /me`.
 *
 * Keys: `auth_method`, `token`, `authToken`, `userRole`, `userId`, `userEmail`,
 * `userDisplayName`, `tenantId`, `tenantName`, `portalUserId`.
 */

const RoleMap: Record<string, string> = {
  'superuser': 'Superuser',
  'tenant-admin': 'TenantAdmin',
  'tenant-user': 'TenantUser',
};

type TokenParsed = {
  sub?: string;
  email?: string;
  preferred_username?: string;
  name?: string;
  azp?: string;
  resource_access?: Record<string, { roles?: string[] }>;
};

export function isSsoActive(): boolean {
  return localStorage.getItem('auth_method') === 'sso';
}

export function mapApiRole(apiRole: string | null | undefined): string {
  return apiRole ? (RoleMap[apiRole.toLowerCase()] ?? '') : '';
}

export function syncFromSso(): void {
  const kc = ssoInstance();
  if (!kc || !kc.authenticated || !kc.token) return;

  const parsed = (kc.tokenParsed ?? {}) as TokenParsed;
  const azp = parsed.azp ?? '';
  const roles = parsed.resource_access?.[azp]?.roles ?? [];
  const role = roles.map((r) => RoleMap[r.toLowerCase()]).find((v) => !!v);

  const email = parsed.email ?? parsed.preferred_username ?? '';
  const displayName = parsed.name?.trim() || email;

  localStorage.setItem('auth_method', 'sso');
  localStorage.setItem('token', kc.token);
  // Same shape the interceptor reads (JSON-encoded raw token string).
  localStorage.setItem('authToken', JSON.stringify(kc.token));
  if (role) localStorage.setItem('userRole', role);
  if (parsed.sub) localStorage.setItem('userId', parsed.sub);
  if (email) localStorage.setItem('userEmail', email);
  if (displayName) localStorage.setItem('userDisplayName', displayName);
}

/** Overwrite role/tenant with what the portal API says (authoritative). */
export function applyPortalIdentity(me: CurrentUser): void {
  localStorage.setItem('userRole', mapApiRole(me.role));
  if (me.id) localStorage.setItem('portalUserId', me.id);
  if (me.fullName) localStorage.setItem('userDisplayName', me.fullName);
  if (me.email) localStorage.setItem('userEmail', me.email);
  if (me.tenantId) {
    localStorage.setItem('tenantId', me.tenantId);
    localStorage.setItem('tenantName', me.tenantName ?? '');
  } else {
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenantName');
  }
}

export function clearAuthSync(): void {
  ['auth_method', 'token', 'authToken', 'userRole', 'userId', 'userEmail',
   'userDisplayName', 'tenantId', 'tenantName', 'portalUserId', 'currentTenantId']
    .forEach((k) => localStorage.removeItem(k));
}
